import React, { useMemo } from "react";

import { fieldsForRole } from "../data/fieldRoles";
import { FieldRole, ReportingField } from "../model/types";

interface FieldSelectProps {
  label: string;
  /** Named `fieldRole` rather than `role` so it is not mistaken for the ARIA attribute. */
  fieldRole: FieldRole;
  value: string;
  fields: ReportingField[];
  onChange: (uuid: string) => void;
}

/**
 * A field-mapping dropdown, with the candidate fields grouped by their scope.
 *
 * Always offers an empty option: the workspace may have no field for a role, and an
 * unmapped role has to be representable rather than silently defaulting to whatever
 * happens to be first in the list.
 */
export default function FieldSelect(props: FieldSelectProps) {
  const { label, fieldRole, value, fields, onChange } = props;

  const groups = useMemo(() => {
    const byScope = new Map<string, ReportingField[]>();
    fieldsForRole(fields, fieldRole).forEach((field) => {
      const group = byScope.get(field.scope) ?? [];
      group.push(field);
      byScope.set(field.scope, group);
    });
    return [...byScope.entries()];
  }, [fields, fieldRole]);

  const isMissing = value === "";

  return (
    <label className="finance-reporting-field">
      <span className="finance-reporting-field__label">
        {label}
        {isMissing && (
          <span className="finance-reporting-field__flag">needs a field</span>
        )}
      </span>
      <select
        className={`finance-reporting-field__control${
          isMissing ? " finance-reporting-field__control--missing" : ""
        }`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">- not mapped -</option>
        {groups.map(([scope, scopeFields]) => (
          <optgroup key={scope} label={scope}>
            {scopeFields.map((field) => (
              <option key={field.uuid} value={field.uuid}>
                {field.name.trim()} - {field.type}
                {field.currencyCode ? ` (${field.currencyCode})` : ""}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}
